import React, { useState } from 'react';
import { withFormik, Field } from 'formik';
import {Alert, Col, Form, FormGroup, Row} from "react-bootstrap";
import PropTypes from "prop-types";
import {rd} from "../../../utils";
import {MARGE_DEPASSEMENT_TTC} from "../utils";

const MyForm = props => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    capLeft,
    hardCap,
  } = props;

  const [submitted, setSubmitted] = useState(false);

  // Dérivé plutôt que stocké : on évite la désync au montage (defaultQuantity
  // déjà au-delà du capLeft sans saisie utilisateur).
  const currentAmount = rd(
    (parseFloat(values.quantity) || 0) * (values.pricePerUnitQuantity || 1),
  );
  // Comparaison à 2 décimales pour rester cohérent avec l'affichage : sinon
  // un capLeft brut type 0.19999999999998863 affiché "0,20€" déclenche le
  // warning sur une saisie de 0,20€.
  const roundedCap =
    capLeft !== undefined && capLeft !== null ? rd(capLeft) : null;
  const isOrderAmountReached =
    roundedCap !== null && currentAmount > roundedCap;

  const handleQuantityChange = React.useCallback((event) => {
    let qty = event.target.value;
    // Pendant la frappe on ne borne QUE le maximum. Appliquer le plancher
    // (minQuantity) à chaque caractère transformerait un "0" intermédiaire
    // — l'utilisateur est en train de taper "0.2" — en minQuantity, ce qui
    // l'empêcherait de poursuivre sa saisie (il resterait coincé sur 0.01).
    // Le plancher est appliqué au blur, une fois la saisie terminée.
    const parsed = parseFloat(qty);
    if (!Number.isNaN(parsed) && parsed > values.maxQuantity) {
      // maxQuantity est calculé en flottant (artefact type 38.449999…),
      // d'où l'arrondi 2 décimales sur ce clamp.
      qty = rd(values.maxQuantity);
    }
    // Permet de re-soumettre si une précédente tentative a échoué côté back.
    setSubmitted(false);
    setFieldValue("quantity", qty);
  }, [values.maxQuantity, setFieldValue]);

  const handleQuantityBlur = React.useCallback((event) => {
    // À la sortie du champ, on borne définitivement dans [min, max] et on
    // arrondit. Un champ vide ou non numérique retombe sur le plancher.
    const parsed = parseFloat(event.target.value);
    let qty;
    if (Number.isNaN(parsed) || parsed < values.minQuantity) {
      qty = values.minQuantity;
    }
    else if (parsed > values.maxQuantity) {
      qty = values.maxQuantity;
    }
    else {
      qty = parsed;
    }
    setFieldValue("quantity", rd(qty));
    handleBlur(event);
  }, [values.minQuantity, values.maxQuantity, setFieldValue, handleBlur]);

  const handleSubmitWithConfirm = (e) => {
    if (submitted) {
      e.preventDefault();
      return;
    }
    if (isOrderAmountReached) {
      // hardCap : pas de marge tolérée — le bouton est déjà disabled mais on
      // verrouille aussi la soumission programmatique.
      if (hardCap) {
        e.preventDefault();
        return;
      }
      const overage = rd(currentAmount - roundedCap);
      const ok = window.confirm(
        `Le montant saisi dépasse de ${overage} € le total de la commande`
        + ` (dans la marge tolérée de ${MARGE_DEPASSEMENT_TTC} €). Confirmer ?`,
      );
      if (!ok) {
        e.preventDefault();
        return;
      }
    }
    setSubmitted(true);
    handleSubmit(e);
  };

  return (
    <Form onSubmit={handleSubmitWithConfirm} className="form-horizontal">
      <FormGroup as={Row} controlId="formHorizontalEmail">
        <Col as={Form.Label} sm={4}>
          {values.labelQuantity ? values.labelQuantity : "Quantité à recréditer"}
        </Col>
        <Col sm={8}>
          <Field
            type="number"
            name="quantity"
            placeholder="Quantité à recréditer"
            className="form-control"
            required
            step="any"
            autoFocus={true}
            onChange={handleQuantityChange}
            onBlur={handleQuantityBlur}
          />
          {errors.quantity && touched.quantity && <div id="feedback">{errors.quantity}</div>}
        </Col>
      </FormGroup>
      <FormGroup as={Row} controlId="formControlsSelect">
        <Col as={Form.Label} sm={4}>
          Type de raison
        </Col>
        <Col sm={8}>
          <select
            name="subtype"
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            required="required"
            value={values.subtype}
          >
            <option value="">choisir</option>
            {values.sub_type_list.map(e => (<option key={e.subtype} value={e.subtype} >{e.label}</option>))}
          </select>
          {errors.subtype && touched.subtype && <div id="feedback">{errors.subtype}</div>}
        </Col>
      </FormGroup>
      <FormGroup as={Row} controlId="formControlsSelect">
        <Col as={Form.Label} sm={4}>
          Explications
        </Col>
        <Col sm={8}>
          <textarea
            name="notes"
            className="form-control"
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {errors.notes && touched.notes && <div id="feedback">{errors.notes}</div>}
        </Col>
      </FormGroup>
      {isOrderAmountReached && (
        <Alert variant={hardCap ? "danger" : "warning"}>
          <strong>ATTENTION :</strong> Le montant saisi dépasse la capacité
          de dédommagement restante de cette commande ({roundedCap} €).
          {hardCap && <> Veuillez réduire la quantité pour pouvoir valider.</>}
        </Alert>
      )}
      <FormGroup>
        <Col sm={12} className="offset-sm-5">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitted || (hardCap && isOrderAmountReached)}
          >
            Créditer {rd(values.pricePerUnitQuantity * values.quantity)} €
          </button>
        </Col>
      </FormGroup>
    </Form>
  );
};

MyForm.propTypes = {
  "values": PropTypes.any,
  "errors": PropTypes.any,
  "touched": PropTypes.any,
  "handleChange": PropTypes.any,
  "handleBlur": PropTypes.any,
  "handleSubmit": PropTypes.any,
};

export const CreditForm = withFormik({
  handleSubmit: (values, formikBag) => {
    formikBag.props.validate(values);
  },
  displayName: 'BasicForm',
})(MyForm);
